class AveyPaymentConfidence extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.product = this.closest('avey-product');
    this.today = this.querySelector('[data-installment-today]');
    this.totals = [...this.querySelectorAll('[data-installment-total]')];
    this.fee = this.querySelector('[data-installment-fee]');
    this.amounts = [...this.querySelectorAll('[data-installment-amount]')];
    this.dates = [...this.querySelectorAll('[data-installment-date]')];
    this.currency = this.dataset.currency || 'EUR';
    this.template = this.dataset.todayTemplate || '__AMOUNT__';
    this.feeTemplate = this.dataset.feeTemplate || '__AMOUNT__';
    this.formatter = this.createCurrencyFormatter();
    this.dateFormatter = this.createDateFormatter();
    this.variantPrices = this.readVariantPrices();

    this.product?.addEventListener('avey:bundle:change', this.onPriceChange);
    this.product?.addEventListener('avey:variant:change', this.onPriceChange);
    this.update(this.selectedTotalCents);
  }

  disconnectedCallback() {
    this.product?.removeEventListener('avey:bundle:change', this.onPriceChange);
    this.product?.removeEventListener('avey:variant:change', this.onPriceChange);
  }

  createCurrencyFormatter() {
    try {
      return new Intl.NumberFormat(this.dataset.numberLocale || document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      return null;
    }
  }

  createDateFormatter() {
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  readVariantPrices() {
    const node = this.querySelector('[data-payment-variant-prices]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  get selectedTotalCents() {
    const selectedBundle = this.product?.querySelector('[data-bundle-card].is-selected');
    const bundleTotal = Number(selectedBundle?.dataset.bundleTotalCents);
    if (Number.isFinite(bundleTotal) && bundleTotal > 0) return bundleTotal;

    const variantId = this.product?.querySelector('[data-variant-id]')?.value;
    const variantTotal = Number(this.variantPrices[variantId]);
    if (Number.isFinite(variantTotal) && variantTotal > 0) return variantTotal;

    const initialTotal = Number(this.dataset.initialTotalCents);
    return Number.isFinite(initialTotal) ? initialTotal : 0;
  }

  onPriceChange = () => {
    this.update(this.selectedTotalCents);
  };

  formatCurrency(cents) {
    return this.formatter
      ? this.formatter.format(cents / 100)
      : `${(cents / 100).toFixed(2)} ${this.currency}`;
  }

  installmentDates() {
    const now = new Date();
    const baseDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
    return [0, 30, 60].map((offset) => {
      const date = new Date(baseDate.getTime());
      date.setDate(date.getDate() + offset);
      return date;
    });
  }

  renderAmountTemplate(target, template, amount) {
    if (!target) return;

    const tokenIndex = template.indexOf('__AMOUNT__');
    if (tokenIndex === -1) {
      target.textContent = template;
      return;
    }

    const amountElement = document.createElement('strong');
    amountElement.textContent = amount;
    target.replaceChildren(
      document.createTextNode(template.slice(0, tokenIndex)),
      amountElement,
      document.createTextNode(template.slice(tokenIndex + '__AMOUNT__'.length)),
    );
  }

  renderToday(amount) {
    this.renderAmountTemplate(this.today, this.template, amount);
  }

  update(totalCents) {
    if (!this.today || !Number.isFinite(totalCents) || totalCents <= 0) return;

    const firstInstallment = Math.round(totalCents / 3);
    const installments = [
      firstInstallment,
      firstInstallment,
      totalCents - (firstInstallment * 2),
    ];
    const dates = this.installmentDates();

    this.renderToday(this.formatCurrency(installments[0]));
    this.renderAmountTemplate(this.fee, this.feeTemplate, this.formatCurrency(0));
    this.totals.forEach((output) => {
      output.textContent = this.formatCurrency(totalCents);
    });

    this.amounts.forEach((output, index) => {
      output.textContent = this.formatCurrency(installments[index] || 0);
    });
    this.dates.forEach((output, index) => {
      output.textContent = this.dateFormatter.format(dates[index]);
    });
  }
}

if (!customElements.get('avey-payment-confidence')) {
  customElements.define('avey-payment-confidence', AveyPaymentConfidence);
}
